import apiClient from './api';

class OrderService {
  async getTables(restaurantId = 1) {
    try {
      const response = await apiClient.get(`/restaurant/${restaurantId}/tables/with-orders/`);
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error('Failed to fetch tables');
      }
    } catch (error) {
      console.error('Error fetching tables:', error);
      throw error;
    }
  }

  async getOrders(filters = {}) {
    try {
      const params = new URLSearchParams();
      if (filters.tableId) params.append('table_id', filters.tableId);
      if (filters.status) params.append('status', filters.status);
      
      const response = await apiClient.get(`/orders/?${params.toString()}`);
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error('Failed to fetch orders');
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      throw error;
    }
  }

  // Orders whose payment isn't settled: everything before COMPLETED and not cancelled.
  async getActiveOrders() {
    try {
      const params = new URLSearchParams();
      ['pending', 'ordered', 'confirmed', 'preparing', 'ready', 'delivered']
        .forEach((s) => params.append('status', s));

      const response = await apiClient.get(`/orders/?${params.toString()}`);
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error('Failed to fetch active orders');
      }
    } catch (error) {
      console.error('Error fetching active orders:', error);
      throw error;
    }
  }

  // Every order of one calendar day, active and finished alike, for the day's All Orders view.
  async getOrdersForDay(date = null) {
    try {
      const d = date || new Date();
      const iso = typeof d === 'string'
        ? d
        : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const response = await apiClient.get(`/orders/?date=${iso}`);
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error('Failed to fetch orders');
      }
    } catch (error) {
      console.error('Error fetching day orders:', error);
      throw error;
    }
  }

  async createOrder(orderData) {
    try {
      const response = await apiClient.post('/orders/create/', orderData);
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to create order');
      }
    } catch (error) {
      console.error('Error creating order:', error);
      throw error;
    }
  }

  // One menu's order-ready content now lives in menuService.getMenuById, so both screens render identically.

  // Complimentary items attached to a dish, tagged by kind, so the order screen can require one pick per kind.
  async getComplimentaryItems(restaurantId, dishId) {
    try {
      const response = await apiClient.get(
        `/restaurant/${restaurantId}/dish/${dishId}/complimentary-items/`,
      );
      return response.data?.success ? (response.data.data || []) : [];
    } catch (error) {
      console.error('Error fetching complimentary items:', error);
      return [];
    }
  }

  // Mark an order cancelled (the order number is retained, not reused).
  async cancelOrder(orderId) {
    try {
      const response = await apiClient.post(`/orders/${orderId}/cancel/`);
      if (response.data.success) return response.data.data;
      throw new Error(response.data.error || 'Failed to cancel order');
    } catch (error) {
      console.error('Error cancelling order:', error);
      throw error;
    }
  }

  // The order's audit trail: every event with its time and actor.
  async getOrderAudit(orderId) {
    try {
      const response = await apiClient.get(`/orders/${orderId}/audit/`);
      if (response.data.success) return response.data.data || [];
      throw new Error(response.data.error || 'Failed to load order history');
    } catch (error) {
      console.error('Error loading order audit:', error);
      throw error;
    }
  }

  async updateOrderStatus(orderId, status) {
    try {
      const response = await apiClient.patch(`/orders/${orderId}/status/`, { status });
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to update order status');
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      throw error;
    }
  }

  // Kitchen status of a single line item (PENDING → PREPARING → READY → SERVED).
  /* `extra` carries what a status needs beyond its name — CANCELLED sends
     { source, reason }, so the order can say WHO called the dish off. */
  async updateOrderItemStatus(orderItemId, status, extra = {}) {
    try {
      const response = await apiClient.patch(`/order-items/${orderItemId}/status/`, { status, ...extra });
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to update item status');
      }
    } catch (error) {
      console.error('Error updating order item status:', error);
      throw error;
    }
  }

  async getOrderDetail(orderId) {
    try {
      const response = await apiClient.get(`/orders/${orderId}/`);
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error('Failed to fetch order details');
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
      throw error;
    }
  }

  async updateTableStatus(tableId, status, occupancyStatus) {
    try {
      const updateData = {};
      if (status) updateData.status = status;
      if (occupancyStatus) updateData.occupancy_status = occupancyStatus;
      
      const response = await apiClient.patch(`/tables/${tableId}/`, updateData);
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error('Failed to update table status');
      }
    } catch (error) {
      console.error('Error updating table status:', error);
      throw error;
    }
  }

  formatOrderForDisplay(order) {
    return {
      id: order.id,
      order_number: order.order_number,
      // The API flattens table_code onto the order; the key here is the
      // screens' label field, kept so every ticket keeps reading it.
      table_number: order.table_code ?? 'Unknown',
      table_id: order.table,
      customer_name: order.customer_name || '',
      // Guest's free-text note to the kitchen ("less spicy", "no onion", …).
      notes: order.notes || '',
      status: (order.status || '').toLowerCase(),
      total_amount: parseFloat(order.total_amount || 0),
      // The booking advance mapped to this sitting: PAID will be deducted at settlement, APPLIED already was.
      advance_amount: parseFloat(order.advance_amount || 0),
      advance_status: order.advance_status || null,
      created_at: order.created_at,
      estimated_time: this.calculateEstimatedTime(order),
      // item_name is the order-time snapshot, `notes` holds instructions, and each item carries its kitchen status.
      items: order.items?.map(item => ({
        id: item.id,
        name: item.item_name || item.dish_name || item.notes || `Item #${item.id}`,
        quantity: item.quantity,
        price: parseFloat(item.unit_price),
        total: parseFloat(item.total_price ?? item.quantity * item.unit_price),
        status: item.status || 'PENDING',
        course: item.course || '',
        notes: item.notes || '',
        // When THIS line reached the kitchen (coursing); null means it came with the order.
        fired_at: item.fired_at || null,
        /* Paid extras and comes-with items — things the kitchen PLATES, not
           just things the guest is billed for. Dropping them here is how a
           ticket for a kebab with three portions of chips read "kebab". */
        addons: Array.isArray(item.addons) ? item.addons : [],
        complimentary: Array.isArray(item.complimentary) ? item.complimentary : [],
        /* Called off, and by which screen — a dish the kitchen refused has to
           read as cancelled wherever the order is printed, not as one still
           on its way. Empty on every line that was not cancelled. */
        cancelled_at: item.cancelled_at || null,
        cancelled_by: item.cancelled_by || '',
        cancel_reason: item.cancel_reason || '',
      })) || []
    };
  }

  calculateEstimatedTime(order) {
    if (!order.created_at) return 'Unknown';
    
    const createdTime = new Date(order.created_at);
    const now = new Date();
    const elapsedMinutes = Math.floor((now - createdTime) / (1000 * 60));
    
    const itemCount = order.items?.length || 1;
    const estimatedTotalTime = Math.max(15, itemCount * 5);
    const remainingTime = estimatedTotalTime - elapsedMinutes;
    
    if (remainingTime <= 0) {
      return 'Ready';
    } else {
      return `${remainingTime} min`;
    }
  }
}

const orderService = new OrderService();
export default orderService;